/**
 * Parse an API timestamp. The backend stores UTC and serializes naive
 * datetimes with no timezone suffix ("2026-07-04T09:03:22.551341") — JS
 * parses offset-less ISO strings as *local* time, shifting every value
 * by the viewer's UTC offset. Append Z when no timezone info is present;
 * strings that already carry Z or ±HH:MM pass through untouched.
 */
export function parseApiDate(dateTimeString) {
  if (!dateTimeString) return null;
  if (dateTimeString instanceof Date) return dateTimeString;

  const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/.test(dateTimeString);
  const date = new Date(hasTimezone ? dateTimeString : `${dateTimeString}Z`);
  return isNaN(date.getTime()) ? null : date;
}

export function parseLocalDate(dateString) {
  if (!dateString) return null;

  if (dateString.includes('T')) {
    dateString = dateString.split('T')[0];
  }

  const parts = dateString.split('-');
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(p => parseInt(p, 10));
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  return new Date(year, month - 1, day);
}

export function formatDate(dateString, options = {}) {
  if (!dateString) return 'N/A';

  const date = parseLocalDate(dateString);
  if (!date) return 'Invalid Date';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  return date.toLocaleDateString('en-US', defaultOptions);
}

export function formatDateShort(dateString) {
  return formatDate(dateString, { year: undefined });
}

export function formatDateLong(dateString) {
  return formatDate(dateString, { month: 'long' });
}

export function formatDateForInput(date) {
  if (!date) return '';

  const d = typeof date === 'string' ? parseLocalDate(date) : date;
  if (!d) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function formatDateTime(dateTimeString) {
  if (!dateTimeString) return 'N/A';

  const date = parseApiDate(dateTimeString);
  if (!date) return 'Invalid Date';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

export function isValidDate(dateString) {
  const date = parseLocalDate(dateString);
  return date !== null && !isNaN(date.getTime());
}

export function getTodayString() {
  return formatDateForInput(new Date());
}

export function daysBetween(dateString1, dateString2) {
  const date1 = parseLocalDate(dateString1);
  const date2 = parseLocalDate(dateString2);

  if (!date1 || !date2) return 0;

  const diffTime = date2.getTime() - date1.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}
